import { Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  FormAutocomplete,
  SaveAndCancelButton,
} from "../../../../components/form";
import { IOSSwitch } from "../../../../components/form/Switch";
import {
  addProfileProductList,
  updateProfileProductList,
} from "../../../../api/profile";
import { APP_COLORS, APP_FONT_FAMILIES } from "../../../../enums";
import { useMutationWithDispatch } from "../../../../hooks/useMutationWithDispatch";
import { useAppSelector } from "../../../../store";
import {
  addOrUpdateProductSchema,
  AddOrUpdateProductFormData,
} from "./config";
import {
  AddOrUpdateProductProps,
  AddOrUpdateProductResponse,
} from "./types";
import {
  getAddPayload,
  getUpdatePayload,
  mapDataToFormValues,
} from "./utils";

export default function AddOrUpdateProduct({
  data,
  onSubmit,
  onCancel,
}: AddOrUpdateProductProps) {
  const lists = useAppSelector((state) => state.lists);

  const categoryOptions = useMemo(
    () => lists?.categories?.data || [],
    [lists?.categories?.data]
  );

  const defaultFormValues = useMemo(() => {
    const base = mapDataToFormValues(data);
    if (base.category && categoryOptions.length) {
      const byLabel = categoryOptions.find(
        (o: any) => o.label === base.category
      );
      const byId = categoryOptions.find((o: any) => o.id === base.category);
      if (byLabel) {
        return { ...base, category: byLabel.id };
      }
      if (byId) {
        return { ...base, category: byId.id };
      }
    }
    return base;
  }, [data?.category, data?.isSatisfied, categoryOptions]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AddOrUpdateProductFormData>({
    resolver: zodResolver(addOrUpdateProductSchema),
    defaultValues: defaultFormValues,
  });

  const { mutateAsync: addProduct, isPending: adding } =
    useMutationWithDispatch<
      AddOrUpdateProductResponse,
      { category: string; isSatisfied: boolean }
    >((payload) => addProfileProductList(payload));

  const { mutateAsync: updateProduct, isPending: updating } =
    useMutationWithDispatch<
      AddOrUpdateProductResponse,
      { id: string; payload: { category: string; isSatisfied: boolean } }
    >(({ id, payload }) => updateProfileProductList(id, payload));

  const isEdit = Boolean(data?.id);
  const submitting = adding || updating;

  const onFormSubmit = async (formData: AddOrUpdateProductFormData) => {
    const payload = isEdit
      ? getUpdatePayload(formData)
      : getAddPayload(formData);
    if (isEdit && data?.id) {
      const res = await updateProduct({ id: data.id, payload });
      if (res?.success) onSubmit();
    } else {
      const res = await addProduct(payload);
      if (res?.success) onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
      <Stack gap={2}>
        <FormAutocomplete
          name="category"
          control={control}
          label="Category"
          required
          options={categoryOptions}
          loading={lists?.categories?.loading}
          valueField="id"
          labelField="label"
          error={errors.category?.message}
        />

        <Controller
          name="isSatisfied"
          control={control}
          render={({ field }) => (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="body"
                component="span"
                fontWeight={600}
                fontFamily={APP_FONT_FAMILIES.ibmPlexMono}
                color={APP_COLORS.textFieldLabelBlack}
              >
                Satisfied
              </Typography>
              <IOSSwitch
                checked={field.value ?? false}
                onChange={(_, checked) => field.onChange(checked)}
                inputProps={{ "aria-label": "Satisfied" }}
              />
            </Stack>
          )}
        />

        <SaveAndCancelButton
          onCancelButtonClick={onCancel}
          loading={submitting}
        />
      </Stack>
    </form>
  );
}
